import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

// Records every route the logged-in user navigates to, plus how long they stayed
// on each page. Identity is resolved server-side from the auth cookie; here we
// only send the path and, when leaving a page, its dwell time.
const ActivityLogger = ({ enabled }: { enabled: boolean }) => {
    const location = useLocation();
    const base = process.env.REACT_APP_BACKEND_URL;
    // Current page's log row id + the moment we entered it.
    const current = useRef<{ id: number | null; enter: number }>({ id: null, enter: Date.now() });

    // Send dwell time for the page we're leaving. keepAlive lets it survive unload.
    const flushDuration = (keepalive = false) => {
        const { id, enter } = current.current;
        if (!id) return;
        const durationMs = Date.now() - enter;
        current.current.id = null; // avoid double-sending
        fetch(`${base}/api/activity/duration`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ id, durationMs }),
            keepalive,
        }).catch(() => {});
    };

    useEffect(() => {
        if (!enabled) return;
        // Leaving the previous page → record its dwell time.
        flushDuration();

        const path = `${location.pathname}${location.search || ""}`;
        current.current = { id: null, enter: Date.now() };

        fetch(`${base}/api/activity/log`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ path, action: "navigate" }),
        })
            .then((r) => r.json())
            .then((res) => { if (res?.id) current.current.id = res.id; })
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, location.search, enabled]);

    // Flush on tab close / hide so the last page's time isn't lost.
    useEffect(() => {
        if (!enabled) return;
        const onHide = () => { if (document.visibilityState === "hidden") flushDuration(true); };
        window.addEventListener("visibilitychange", onHide);
        window.addEventListener("pagehide", () => flushDuration(true));
        return () => window.removeEventListener("visibilitychange", onHide);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    return null;
};

export default ActivityLogger;
